import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Zap } from "lucide-react";
import MediaUploadInput from "@/components/MediaUploadInput";
import GroupsMultiSelect from "@/components/GroupsMultiSelect";

const EMPTY = {
  keywords: [], keyword: "",
  match_type: "contains", response_type: "text",
  response_content: "", media_url: "", scope: "both", is_active: true,
};

// Small chip-input that turns "precio, pago" or Enter / Tab into chips.
function KeywordChips({ value, onChange, testIdPrefix = "kw-chips" }) {
  const [draft, setDraft] = React.useState("");
  const list = Array.isArray(value) ? value : [];
  const addFromDraft = () => {
    const parts = draft.split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const merged = [...list];
    parts.forEach((p) => { if (!merged.some((x) => x.toLowerCase() === p.toLowerCase())) merged.push(p); });
    onChange(merged.slice(0, 25));
    setDraft("");
  };
  return (
    <div data-testid={`${testIdPrefix}-wrap`}>
      <div className="border rounded-md px-2 py-2 min-h-[42px] bg-white flex flex-wrap gap-1 items-center focus-within:border-slate-400">
        {list.map((kw, idx) => (
          <Badge key={`${kw}-${idx}`} variant="secondary" className="text-xs pl-2 pr-1 gap-1" data-testid={`${testIdPrefix}-chip-${idx}`}>
            <span className="max-w-[180px] truncate">{kw}</span>
            <button
              type="button"
              onClick={() => onChange(list.filter((_, i) => i !== idx))}
              className="ml-0.5 rounded-full hover:bg-slate-300/60 px-0.5 text-slate-700"
              data-testid={`${testIdPrefix}-chip-remove-${idx}`}
              aria-label="Quitar"
            >×</button>
          </Badge>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
              e.preventDefault(); addFromDraft();
            } else if (e.key === "Backspace" && !draft && list.length) {
              e.preventDefault(); onChange(list.slice(0, -1));
            }
          }}
          onBlur={addFromDraft}
          placeholder={list.length === 0 ? "precio, pago, horario… (Enter para agregar)" : "+ agregar"}
          className="flex-1 min-w-[120px] outline-none text-sm bg-transparent px-1 py-1"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Cualquiera de estas {list.length || 0} palabras dispara la <strong>misma</strong> respuesta. Separá con coma o Enter.
      </p>
    </div>
  );
}

export default function KeywordTriggersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchItems = useCallback(async () => {
    try { const { data } = await authAxios.get(`${API}/keyword-triggers`); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const save = async () => {
    // Combine chip list with anything still in the keyword text input
    const kwArr = Array.isArray(form.keywords) ? form.keywords.slice() : [];
    if (form.keyword && form.keyword.trim() && !kwArr.some((x) => x.toLowerCase() === form.keyword.trim().toLowerCase())) {
      kwArr.push(form.keyword.trim());
    }
    if (!kwArr.length) { alert("Agregá al menos una palabra clave"); return; }
    const payload = { ...form, keywords: kwArr, keyword: kwArr[0] };
    if (editing) await authAxios.put(`${API}/keyword-triggers/${editing}`, payload);
    else await authAxios.post(`${API}/keyword-triggers`, payload);
    setOpen(false); fetchItems();
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar trigger?")) return;
    await authAxios.delete(`${API}/keyword-triggers/${id}`); fetchItems();
  };

  const toggle = async (t) => {
    await authAxios.put(`${API}/keyword-triggers/${t.id}`, { ...t, is_active: !t.is_active });
    fetchItems();
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="keywords-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Zap className="w-7 h-7" /> Palabras Clave
          </h1>
          <p className="text-slate-500 mt-1">Respuestas automáticas cuando el cliente mencione una palabra</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="kw-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva Palabra Clave
        </Button>
      </div>

      {loading ? <div className="animate-pulse h-40 bg-slate-200 rounded-xl" /> :
       items.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">
          <Zap className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Aún no hay palabras clave.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map(t => (
            <Card key={t.id} data-testid={`kw-card-${t.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {((t.keywords && t.keywords.length) ? t.keywords : [t.keyword]).filter(Boolean).map((kw, i) => (
                      <code key={`${t.id}-kw-${i}`} className="font-mono bg-slate-100 px-2 py-0.5 rounded text-sm">{kw}</code>
                    ))}
                    <Badge variant="secondary" className="text-xs">{t.match_type}</Badge>
                    <Badge variant="secondary" className="text-xs">{t.scope}</Badge>
                    {t.response_type !== "text" && <Badge variant="secondary" className="text-xs">{t.response_type}</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-2">{t.response_content}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Switch checked={!!t.is_active} onCheckedChange={() => toggle(t)} data-testid={`kw-toggle-${t.id}`} />
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => {
                        setEditing(t.id);
                        const kws = (t.keywords && t.keywords.length) ? t.keywords : (t.keyword ? [t.keyword] : []);
                        setForm({ ...EMPTY, ...t, keywords: kws, keyword: kws[0] || "" });
                        setOpen(true);
                      }} data-testid={`kw-edit-${t.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(t.id)} data-testid={`kw-delete-${t.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Trigger" : "Nueva Palabra Clave"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Palabras clave * <span className="text-xs text-slate-500 font-normal">(podés poner varias)</span></Label>
              <KeywordChips
                value={form.keywords && form.keywords.length ? form.keywords : (form.keyword ? [form.keyword] : [])}
                onChange={(arr) => setForm({ ...form, keywords: arr, keyword: arr[0] || "" })}
                testIdPrefix="kw-chips"
              />
            </div>
            <div><Label>Tipo de coincidencia</Label>
              <Select value={form.match_type} onValueChange={v => setForm({ ...form, match_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">Contiene</SelectItem>
                  <SelectItem value="exact">Exacta</SelectItem>
                  <SelectItem value="starts_with">Empieza con</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Alcance</Label>
              <Select value={form.scope} onValueChange={v => setForm({ ...form, scope: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Privado y Grupos</SelectItem>
                  <SelectItem value="private">Solo chats privados</SelectItem>
                  <SelectItem value="group">Solo grupos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(form.scope === "group" || form.scope === "both") && (
              <GroupsMultiSelect
                value={form.group_chat_ids || []}
                onChange={(ids) => setForm({ ...form, group_chat_ids: ids })}
                label="¿En qué grupos aplica esta palabra clave?"
                helpText="Vacío = aplica en TODOS los grupos."
                testIdPrefix="kw-groups"
              />
            )}
            <div><Label>Tipo de respuesta</Label>
              <Select value={form.response_type} onValueChange={v => setForm({ ...form, response_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="image">Imagen</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="document">Documento (PDF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Respuesta</Label>
              <Textarea rows={3} value={form.response_content} onChange={e => setForm({ ...form, response_content: e.target.value })} data-testid="kw-content" />
            </div>
            {form.response_type !== "text" && (
              <div><Label>Archivo / URL del medio</Label>
                <MediaUploadInput
                  value={form.media_url}
                  onChange={(v) => setForm({ ...form, media_url: v })}
                  accept={form.response_type === "image" ? "image/*" :
                          form.response_type === "video" ? "video/*" :
                          form.response_type === "audio" ? "audio/*" :
                          "application/pdf"}
                  testIdPrefix="kw-media"
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Activo</Label>
              <Switch checked={!!form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="kw-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
